import React, {useEffect, useState, useCallback, useRef} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {Analytics} from "@vercel/analytics/react";
import {
  LoadScript,
  GoogleMap,
  Marker,
  InfoWindow,
  Autocomplete,
} from "@react-google-maps/api";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  arrayUnion,
  increment,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import {getStorage, ref, uploadBytes, getDownloadURL} from "firebase/storage";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LanguageIcon from "@mui/icons-material/Language";
import CategoryIcon from "@mui/icons-material/Category";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import {auth} from "./firebase";
import {signInWithPopup, GoogleAuthProvider, signOut} from "firebase/auth";
import {db} from "./firebase";
import AddHouseForm from "./components/AddHouseForm";
import {
  Fab,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
} from "@mui/material";
import PaidIcon from "@mui/icons-material/AttachMoney";
import FreeIcon from "@mui/icons-material/MoneyOff";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RatingComponent from "./components/RatingComponent";
import TopPicks from "./components/TopPicks";
import HelpButton from "./components/HelpButton";
import HelpPage from "./components/HelpPage";
import {NavBar} from "./components/Navbar";
import CategoryFilter from "./components/CategoryFilter";
import CategoryDropdownWeb from "./components/CategoryDropdownWeb";
import {Close} from "@mui/icons-material";
import RouteIcon from "@mui/icons-material/Route";
import Login from "./components/Login";
import LandingPage from "./components/LandingPage";
import RoutePlanner from "./components/RoutePlanner";
import Footer from "./components/Footer";

const libraries = ["places"];
const mapStyles = [
  {featureType: "poi", stylers: [{visibility: "off"}]},
  {featureType: "transit", stylers: [{visibility: "off"}]},
  {featureType: "road", elementType: "labels", stylers: [{visibility: "on"}]},
  {featureType: "road", elementType: "geometry", stylers: [{color: "#ffffff"}]},
  {featureType: "landscape", stylers: [{visibility: "on"}]},
  {featureType: "administrative", stylers: [{visibility: "on"}]},
];

const App = () => {
  const [locationError, setLocationError] = useState(false); // Track if location is unavailable
  const [zoomLevel, setZoomLevel] = useState(11); // Default zoom level

  const [houses, setHouses] = useState([]);
  const [housesLoaded, setHousesLoaded] = useState(false);
  const [markerImagesLoaded, setMarkerImagesLoaded] = useState(false);
  const [firebaseError, setFirebaseError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [user, setUser] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [mapCenter, setMapCenter] = useState({lat: 49.131, lng: -122.312});
  const [selectedHouse, setSelectedHouse] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [mapExpanded, setMapExpanded] = useState(false);
  const storage = getStorage(); // Initialize Firebase Storage
  const [ratingsModalOpen, setRatingsModalOpen] = useState(false);
  const [ratingsHouse, setRatingsHouse] = useState(null);
  const [locationReviews, setLocationReviews] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    // Check if banner was previously dismissed
    return localStorage.getItem('infoBannerDismissed') === 'true';
  }); // Reviews from reviews collection
  const [hasUserVoted, setHasUserVoted] = useState(false); // Track if user has voted
  const [autocomplete, setAutocomplete] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedLegend, setSelectedLegend] = useState("all"); // Default to "all"
  const [selectedCategories, setSelectedCategories] = useState(["all"]);
  
  // Route planning state
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [routePlannerOpen, setRoutePlannerOpen] = useState(false);
  const [fabPulse, setFabPulse] = useState(false);
  const isProcessingRouteToggle = useRef(false); // Prevent double-clicks
  const lastToggleTime = useRef(0); // Track last toggle time
  const touchHandled = useRef(false); // Track if touch event was handled

  // Handler for selecting a legend
  const handleLegendClick = (legend) => {
    if (legend === "selected") {
      // Toggle the "Selected" filter
      setSelectedLegend((prev) => (prev === "selected" ? "all" : "selected"));
    } else {
      // Toggle other filters
      setSelectedLegend((prev) => (prev === legend ? "all" : legend));
    }
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };
  const [review, setReview] = useState("");
  const handleReviewChange = (event) => {
    setReview(event.target.value);
  };
  const handleViewRatings = async (house) => {
    setSelectedHouse(house);
    setRatingsHouse(house);
    setRatingsModalOpen(true);
    
    // Fetch reviews from reviews collection (matching app's structure)
    try {
      const reviewsQuery = query(
        collection(db, "reviews"),
        where("locationId", "==", house.id),
        orderBy("createdAt", "desc")
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const reviews = [];
      reviewsSnapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });
      setLocationReviews(reviews);
    } catch (error) {
      // Fallback to reviews array on location if query fails
      setLocationReviews(house.reviews ? house.reviews.map((r, i) => ({ id: `fallback-${i}`, comment: typeof r === 'string' ? r : r.comment || r })) : []);
    }
    
    // Check if user has voted for this location
    if (user) {
      try {
        const votesQuery = query(
          collection(db, "votes"),
          where("locationId", "==", house.id),
          where("userId", "==", user.uid)
        );
        const votesSnapshot = await getDocs(votesQuery);
        setHasUserVoted(!votesSnapshot.empty);
      } catch (error) {
        setHasUserVoted(false);
      }
    } else {
      setHasUserVoted(false);
    }
  };

  const handleCloseRatingsModal = () => {
    setRatingsModalOpen(false);
    setRatingsHouse(null);
    setLocationReviews([]);
    setHasUserVoted(false);
  };

  useEffect(() => {
    const fallbackLocation = {lat: 49.1457, lng: -122.3077}; // Default to Mission Library
    let watchId;

    const startTracking = () => {
      if (navigator.geolocation) {
        watchId = navigator.geolocation.watchPosition(
          (position) => {
            const {latitude, longitude} = position.coords;
            const newLocation = {lat: latitude, lng: longitude};
            setCurrentLocation(newLocation);
          },
          (error) => {
            setCurrentLocation(fallbackLocation);
          },
          {enableHighAccuracy: true, maximumAge: 0, timeout: 10000}
        );
      } else {
        setCurrentLocation(fallbackLocation);
      }
    };

    startTracking();

    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []); // Empty dependency array ensures this runs once on mount.

  // Check Firebase configuration on mount
  useEffect(() => {
    const requiredVars = [
      'REACT_APP_FIREBASE_API_KEY',
      'REACT_APP_FIREBASE_AUTH_DOMAIN',
      'REACT_APP_FIREBASE_PROJECT_ID',
      'REACT_APP_FIREBASE_STORAGE_BUCKET',
      'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
      'REACT_APP_FIREBASE_APP_ID'
    ];
    
    const missing = requiredVars.filter(v => !process.env[v]);
    if (missing.length > 0) {
      const errorMsg = `Missing environment variables: ${missing.join(', ')}`;
      setFirebaseError(errorMsg);
    } else {
      setFirebaseError(null);
    }
  }, []);

  //   const fallbackLocation = {lat: 49.131, lng: -122.312}; // Default to Mission, BC
  //   let watchId;

  //   const startTracking = () => {
  //     if (navigator.geolocation) {
  //       watchId = navigator.geolocation.watchPosition(
  //         (position) => {
  //           const {latitude, longitude} = position.coords;
  //           const newLocation = {lat: latitude, lng: longitude};
  //           console.log("Current location updated:", newLocation);

  //           setCurrentLocation((prevLocation) => {
  //             // Only update `mapCenter` if it hasn't been manually adjusted
  //             if (!prevLocation) {
  //               setMapCenter(newLocation); // Center on initial load
  //             }
  //             return newLocation;
  //           });

  //           setLocationError(false); // Clear error if successful
  //         },
  //         (error) => {
  //           console.warn("Geolocation error:", error.message);
  //           setLocationError(true); // Show error
  //           if (!currentLocation) {
  //             // Use fallback location only if no current location is set
  //             setCurrentLocation(fallbackLocation);
  //             setMapCenter(fallbackLocation);
  //           }
  //         },
  //         {
  //           enableHighAccuracy: true,
  //           maximumAge: 0,
  //           timeout: 10000,
  //         }
  //       );
  //     } else {
  //       console.error("Geolocation not supported");
  //       setLocationError(true);
  //       if (!currentLocation) {
  //         // Use fallback location only if no current location is set
  //         setCurrentLocation(fallbackLocation);
  //         setMapCenter(fallbackLocation);
  //       }
  //     }
  //   };

  //   startTracking();

  //   return () => {
  //     if (watchId) {
  //       navigator.geolocation.clearWatch(watchId);
  //     }
  //   };
  // }, [currentLocation]); // Add `currentLocation` to dependency array

  //   const fallbackLocation = {lat: 49.131, lng: -122.312}; // Default to Mission, BC

  //   let watchId;

  //   const startTracking = () => {
  //     if (navigator.geolocation) {
  //       watchId = navigator.geolocation.watchPosition(
  //         (position) => {
  //           const {latitude, longitude} = position.coords;
  //           const newLocation = {lat: latitude, lng: longitude};
  //           console.log("Current location updated while driving:", newLocation); // Debugging
  //           setCurrentLocation(newLocation);
  //           setMapCenter(newLocation);
  //         },
  //         (error) => {
  //           console.warn("Geolocation error:", error.message);
  //           setCurrentLocation(fallbackLocation);
  //           setMapCenter(fallbackLocation);
  //         },
  //         {
  //           enableHighAccuracy: true, // Use GPS for high accuracy
  //           maximumAge: 0, // No cached location
  //           timeout: 10000, // Timeout after 10 seconds
  //         }
  //       );
  //     } else {
  //       console.error("Geolocation not supported");
  //       setCurrentLocation(fallbackLocation);
  //       setMapCenter(fallbackLocation);
  //     }
  //   };

  //   startTracking();

  //   // Cleanup the watcher on unmount
  //   return () => {
  //     if (watchId) {
  //       navigator.geolocation.clearWatch(watchId);
  //     }
  //   };
  // }, []);
  // useEffect(() => {
  //   if (currentLocation) {
  //     setMapCenter(currentLocation);
  //     console.log("Map recentered to:", currentLocation); // Debugging
  //   }
  // }, [currentLocation]);

  // Initial fetch - only if not on map page
  useEffect(() => {
    if (location.pathname !== "/map") {
    const fetchHouses = async () => {
      try {
          const querySnapshot = await getDocs(collection(db, "locations"));
          
          const fetchedHouses = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            // Convert app's format (latitude/longitude) to website's format (location: {lat, lng})
            return {
          id: doc.id,
              ...data,
              location: data.location || (data.latitude && data.longitude 
                ? { lat: data.latitude, lng: data.longitude }
                : null),
            };
          }).filter(house => house.location && house.location.lat && house.location.lng);
          
        setHouses(fetchedHouses);
      } catch (error) {
          setHouses([]);
      }
    };

    fetchHouses();
    }
  }, [location.pathname]);

  // Handle category from URL query parameter
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category");

  // When user navigates to map page, fetch houses
  useEffect(() => {
    if (location.pathname === "/map") {
      // Set category from URL if present
      if (categoryFromUrl && categoryFromUrl !== "all") {
        setSelectedCategories([categoryFromUrl]);
      }
      
      // Reset houses loaded state
      setHousesLoaded(false);
      
      const fetchHouses = async () => {
        try {
          // Try to get collection reference
          const locationsRef = collection(db, "locations");
          
          // Execute query
          const querySnapshot = await getDocs(locationsRef);
          
          const fetchedHouses = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            // Convert app's format (latitude/longitude) to website's format (location: {lat, lng})
            const location = data.location || (data.latitude && data.longitude 
              ? { lat: data.latitude, lng: data.longitude }
              : null);
            
            return {
              id: doc.id,
              ...data,
              location,
            };
          });
          
          // Filter out locations without valid coordinates
          const validHouses = fetchedHouses.filter(house => house.location && house.location.lat && house.location.lng);
          
          setHouses(validHouses);
          // Set loaded after a small delay to ensure map is ready
          setTimeout(() => {
            setHousesLoaded(true);
          }, 300);
        } catch (error) {
          // Set empty array and mark as loaded to prevent infinite loading
          setHouses([]);
          setHousesLoaded(true);
        }
      };

      fetchHouses();
    } else {
      setHousesLoaded(false);
    }
  }, [location.pathname, categoryFromUrl]);


  const handleLogin = () => navigate("/login");
  // const handleLogin = async () => {
  //   try {
  //     const provider = new GoogleAuthProvider();
  //     const result = await signInWithPopup(auth, provider);

  //     // Get the signed-in user's information
  //     const loggedInUser = result.user;
  //     setUser(loggedInUser);

  //     console.log("Login successful:", loggedInUser);
  //     setSnackbarMessage("Login successful!");
  //     setShowSnackbar(true);
  //   } catch (error) {
  //     console.error("Login failed:", error.message);

  //     // Display error message to the user
  //     setSnackbarMessage(`Login failed: ${error.message}`);
  //     setShowSnackbar(true);
  //   }
  // };
  // Create numbered marker icon
  const createNumberedMarkerIcon = (number) => {
    const canvas = document.createElement('canvas');
    canvas.width = 50;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    
    // Draw circle background
    ctx.beginPath();
    ctx.arc(25, 25, 22, 0, 2 * Math.PI);
    ctx.fillStyle = '#4CAF50'; // Green color
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Draw number
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(number.toString(), 25, 25);
    
    return canvas.toDataURL();
  };

  const getMarkerIcon = (category, isSelected, routeNumber = null) => {
    // If location is in route, show numbered marker
    if (routeNumber !== null) {
      return createNumberedMarkerIcon(routeNumber);
    }

    // Map categories to local icon files (matching app's EXACT icon mapping)
    // This matches the app's MapView.js getIconSource function
    const categoryIconMap = {
      house: "/markers/house.png",
      community: "/markers/community.png",
      event: "/markers/events.png",
      events: "/markers/events.png", // Handle both "event" and "events"
      market: "/markers/market.png",
      charity: "/markers/charity.png",
      santa: "/markers/santa.png",
      parade: "/markers/parade.png",
      show: "/markers/shows.png", // App uses "shows" in icon map
      shows: "/markers/shows.png", // Handle both "show" and "shows"
      skating: "/markers/skating.png",
      restaurant: "/markers/dining.png", // App uses "dining" in icon map
      dining: "/markers/dining.png", // Handle both "restaurant" and "dining"
      tree_farm: "/markers/christmastree_or_all.png",
      shopping: "/markers/market.png", // Reuse market icon
      all: "/markers/christmastree_or_all.png", // Default (matches app)
    };

    // Normalize category name (handle variations)
    const normalizedCategory = category?.toLowerCase() || "all";

    return categoryIconMap[normalizedCategory] || categoryIconMap.all;
  };

  // Preload marker images to ensure they're ready before rendering markers
  useEffect(() => {
    if (location.pathname === "/map") {
      setMarkerImagesLoaded(false);
      const markerImages = [
        "/markers/house.png",
        "/markers/community.png",
        "/markers/events.png",
        "/markers/market.png",
        "/markers/charity.png",
        "/markers/santa.png",
        "/markers/parade.png",
        "/markers/shows.png",
        "/markers/skating.png",
        "/markers/dining.png",
        "/markers/christmastree_or_all.png",
        "https://img.icons8.com/color/48/000000/christmas-star.png", // Selected marker
      ];

      const loadImage = (src) => {
        return new Promise((resolve, reject) => {
          // Check if image is already cached
          const img = new Image();
          img.onload = () => {
            // Verify image actually loaded by checking dimensions
            if (img.width > 0 && img.height > 0) {
              resolve();
            } else {
              reject(new Error(`Image ${src} loaded but has invalid dimensions`));
            }
          };
          img.onerror = () => {
            // Still resolve to not block rendering
            resolve();
          };
          img.src = src;
        });
      };

      // Load all images and wait for them to be ready
      Promise.all(markerImages.map(loadImage))
        .then(() => {
          // Small delay to ensure images are fully ready for Google Maps
          setTimeout(() => {
            setMarkerImagesLoaded(true);
          }, 100);
        })
        .catch((error) => {
          // Still set to true after a delay to not block rendering indefinitely
          setTimeout(() => {
            setMarkerImagesLoaded(true);
          }, 500);
        });
    } else {
      setMarkerImagesLoaded(false);
    }
  }, [location.pathname]);

  const handleClearFilters = () => {
    setFilter("all"); // Reset paid/free filter
    setSelectedLegend("all"); // Reset category filter
    setSnackbarMessage("All filters cleared!");
    setShowSnackbar(true); // Provide feedback to the user
  };
  // const MarkerLegend = () => (
  //   <div
  //     style={{
  //       position: "absolute",
  //       bottom: "10px",
  //       left: "10px",
  //       backgroundColor: "white",
  //       padding: "10px",
  //       borderRadius: "8px",
  //       boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  //       zIndex: 1000,
  //     }}
  //   >
  //     <div style={{display: "flex", alignItems: "center", marginTop: "5px"}}>
  //       <img
  //         src="https://img.icons8.com/fluency/48/000000/home.png"
  //         alt="House"
  //         style={{width: "24px", height: "24px", marginRight: "5px"}}
  //       />
  //       <span style={{fontSize: "12px"}}>House</span>
  //     </div>
  //     <div style={{display: "flex", alignItems: "center", marginTop: "5px"}}>
  //       <img
  //         src="https://img.icons8.com/color/48/000000/city.png"
  //         alt="Community Lights"
  //         style={{width: "24px", height: "24px", marginRight: "5px"}}
  //       />
  //       <span style={{fontSize: "12px"}}>Community Lights</span>
  //     </div>
  //     <div style={{display: "flex", alignItems: "center", marginTop: "5px"}}>
  //       <img
  //         src="https://img.icons8.com/color/48/000000/calendar.png"
  //         alt="Events"
  //         style={{width: "24px", height: "24px", marginRight: "5px"}}
  //       />
  //       <span style={{fontSize: "12px"}}>Events</span>
  //     </div>
  //     <div style={{display: "flex", alignItems: "center", marginTop: "5px"}}>
  //       <img
  //         src="https://img.icons8.com/fluency/48/000000/christmas-star.png"
  //         alt="Selected"
  //         style={{width: "24px", height: "24px", marginRight: "5px"}}
  //       />
  //       <span style={{fontSize: "12px"}}>Selected Location</span>
  //     </div>
  //   </div>
  // );

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setSnackbarMessage("You have been logged out.");
      setShowSnackbar(true);
    } catch (error) {
      // Logout error handled silently
    }
  };
  const extractCityFromAddress = (address) => {
    if (!address) return "Unknown City";

    // Split the address into parts by commas
    const addressParts = address.split(",");

    // Reverse the array for easier parsing
    const reversedParts = addressParts.reverse();

    // Find a valid city part (skip postal code and country parts)
    for (let part of reversedParts) {
      const trimmedPart = part.trim();

      // Exclude postal codes or country-like names
      if (!/^\d/.test(trimmedPart) && trimmedPart.length > 2) {
        return trimmedPart;
      }
    }

    return "Unknown City"; // Default fallback
  };
  const handleAddHouse = async (newHouse, isUpdate = false) => {
    // If newHouse is null, user needs to login (handled by form)
    if (!newHouse) {
      navigate("/login?redirect=/add-house");
      return null;
    }

    if (!user) {
      alert("Please log in to add a location. You'll be redirected to the login page.");
      navigate("/login?redirect=/add-house");
      return null;
    }

    try {
      if (isUpdate && newHouse.id) {
        // Update mode: update images for existing location
        const locationRef = doc(db, "locations", newHouse.id);
        await updateDoc(locationRef, {
          images: newHouse.images || [],
        });
        return newHouse.id;
      } else {
        // Add new location
      const city = extractCityFromAddress(newHouse.address);
        const locationData = {
        ...newHouse,
          latitude: newHouse.latitude || (newHouse.location?.lat || 0),
          longitude: newHouse.longitude || (newHouse.location?.lng || 0),
        city,
        ownerId: user.uid,
          userId: user.uid, // Also add userId for app compatibility
          votes: newHouse.votes || 0,
          verified: newHouse.verified || false,
          images: newHouse.images || [],
          createdAt: newHouse.createdAt || new Date().toISOString()
        };
        // Remove location object if it exists (app uses latitude/longitude)
        delete locationData.location;
        delete locationData.id; // Remove id if present (it will be generated)
        
        const docRef = await addDoc(collection(db, "locations"), locationData);
        const locationId = docRef.id;

        // Refresh the houses list
        const querySnapshot = await getDocs(collection(db, "locations"));
        const fetchedHouses = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            location: data.location || (data.latitude && data.longitude 
              ? { lat: data.latitude, lng: data.longitude }
              : null),
          };
        });
        setHouses(fetchedHouses);

        setSnackbarMessage("Location added successfully!");
      setShowSnackbar(true);
        
        // Navigate back to map after successful add
        navigate("/map");
        
        return locationId;
      }
    } catch (error) {
      alert("Failed to add location. Please try again.");
      throw error;
    }
  };

  const handleAddReview = async () => {
    if (!user) {
      alert("Please log in to leave a review. You'll be redirected to the login page.");
      handleCloseRatingsModal();
      navigate("/login?redirect=/map");
      return;
    }

    if (!selectedHouse || !selectedHouse.id) {
      alert("No house selected. Please select a house first.");
      return;
    }

    if (!review.trim()) {
      alert("Review cannot be empty.");
      return;
    }

    try {
      // Store review in reviews collection (matching app's structure)
      const reviewData = {
        locationId: selectedHouse.id,
        locationName: selectedHouse.name || selectedHouse.address,
        rating: null, // Website doesn't have rating, but app structure supports it
        comment: review.trim(),
        userId: user.uid,
        photoUrl: null, // Website doesn't support photo upload in reviews yet
        helpfulCount: 0,
        createdAt: new Date().toISOString(),
      };

      await addDoc(collection(db, "reviews"), reviewData);

      // Also update the location document's reviews array for backward compatibility
      // (in case the app reads from both places)
      const houseDoc = doc(db, "locations", selectedHouse.id);
      await updateDoc(houseDoc, {reviews: arrayUnion(review.trim())});

      // Refresh reviews from database
      const reviewsQuery = query(
        collection(db, "reviews"),
        where("locationId", "==", selectedHouse.id),
        orderBy("createdAt", "desc")
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const updatedReviews = [];
      reviewsSnapshot.forEach((doc) => {
        updatedReviews.push({ id: doc.id, ...doc.data() });
      });
      setLocationReviews(updatedReviews);

      // Update local state
      setHouses((prev) =>
        prev.map((h) =>
          h.id === selectedHouse.id
            ? {...h, reviews: [...(h.reviews || []), review.trim()]}
            : h
        )
      );

      setReview("");
      setSnackbarMessage("Review added successfully!");
      setShowSnackbar(true);
      // Don't close modal, just refresh reviews
    } catch (error) {
      alert("Failed to submit review. Please try again.");
    }
  };

  const handleMarkerClick = (house, event) => {
    // Prevent any default behavior (like opening images or navigating)
    if (event && event.domEvent) {
      event.domEvent.preventDefault();
      event.domEvent.stopPropagation();
      if (event.domEvent.stopImmediatePropagation) {
        event.domEvent.stopImmediatePropagation();
      }
    }
    
    // Small delay to ensure event is fully handled before opening dialog
    setTimeout(() => {
      // Check if Ctrl/Cmd key is pressed for route selection
      if (event && event.domEvent && (event.domEvent.ctrlKey || event.domEvent.metaKey)) {
        toggleLocationInRoute(house);
      } else {
        // Simply open the location info dialog
        handleViewRatings(house);
      }
    }, 0);
  };

  const toggleLocationInRoute = useCallback((house) => {
    setSelectedLocations((prev) => {
      const isSelected = prev.some((loc) => loc.id === house.id);
      if (isSelected) {
        return prev.filter((loc) => loc.id !== house.id);
      } else {
        return [...prev, house];
      }
    });
    // Don't auto-open route planner - user should click the FAB button
  }, []);

  // Handler for adding location to route from details dialog - optimized for speed and mobile
  const handleAddToRouteFromDialog = useCallback((house, event, isTouch = false) => {
    const now = Date.now();
    
    // Prevent double-clicks - check both ref and time difference
    if (isProcessingRouteToggle.current || (now - lastToggleTime.current < 1000)) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
        if (event.nativeEvent) {
          event.nativeEvent.stopImmediatePropagation();
        }
      }
      return;
    }
    
    // If this is a click event and we already handled a touch, ignore it
    if (!isTouch && touchHandled.current) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      touchHandled.current = false; // Reset for next interaction
      return;
    }
    
    // Set flags IMMEDIATELY - synchronously before any state updates
    isProcessingRouteToggle.current = true;
    lastToggleTime.current = now;
    if (isTouch) {
      touchHandled.current = true;
    }
    
    // Use functional update to get current state and update atomically
    setSelectedLocations((prev) => {
      const currentlySelected = prev.some((loc) => loc.id === house.id);
      const willBeInRoute = !currentlySelected;
      
      // Calculate new state
      const newState = currentlySelected
        ? prev.filter((loc) => loc.id !== house.id)
        : [...prev, house];
      
      // Show feedback immediately (synchronous)
      setSnackbarMessage(
        willBeInRoute 
          ? "Location added to route" 
          : "Location removed from route"
      );
      setShowSnackbar(true);
      
      // Close dialog immediately if adding
      if (willBeInRoute) {
        handleCloseRatingsModal();
        setFabPulse(true);
        setTimeout(() => setFabPulse(false), 2000);
      }
      
      // Reset flags after delay
      setTimeout(() => {
        isProcessingRouteToggle.current = false;
        touchHandled.current = false;
      }, 1000);
      
      return newState;
    });
  }, []);

  const handleRemoveLocation = (locationId) => {
    setSelectedLocations((prev) => prev.filter((loc) => loc.id !== locationId));
  };

  const handleClearAllLocations = () => {
    setSelectedLocations([]);
  };

  // Helper function to calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  };

  // Function to optimize route by sorting locations by distance from start point
  const optimizeRoute = (startLat, startLng, locations) => {
    // Create array with locations and their distances
    const locationsWithDistance = locations.map(location => ({
      ...location,
      distance: calculateDistance(
        startLat,
        startLng,
        location.location.lat,
        location.location.lng
      )
    }));

    // Sort by distance (closest first)
    return locationsWithDistance.sort((a, b) => a.distance - b.distance);
  };

  const handleOpenInGoogleMaps = () => {
    if (selectedLocations.length === 0) {
      setSnackbarMessage("Please select at least one location!");
      setShowSnackbar(true);
      return;
    }

    // Function to build and open Google Maps URL with optimized route
    const openGoogleMapsRoute = (userLat, userLng) => {
      // Optimize route: sort locations by distance from current location
      const optimizedLocations = optimizeRoute(userLat, userLng, selectedLocations);
      
      // Build Google Maps URL with optimized waypoints
      let waypoints = "";
      
      // Add optimized locations as waypoints (closest first)
      optimizedLocations.forEach((location, index) => {
        const lat = location.location.lat;
        const lng = location.location.lng;
        waypoints += `${lat},${lng}`;
        if (index < optimizedLocations.length - 1) {
          waypoints += "/";
        }
      });

      // Build URL with proper format for auto-starting navigation on mobile
      if (optimizedLocations.length > 1) {
        // Multiple waypoints - use waypoints parameter
        const waypointsArray = waypoints.split('/');
        const destination = waypointsArray[waypointsArray.length - 1];
        const waypointsParam = waypointsArray.slice(0, -1).join('|');
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${destination}&waypoints=${waypointsParam}&dir_action=navigate&travelmode=driving`;
        window.open(googleMapsUrl, "_blank");
      } else {
        // Single destination
        const destination = waypoints;
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${destination}&dir_action=navigate&travelmode=driving`;
        window.open(googleMapsUrl, "_blank");
      }
      
      setSnackbarMessage("Starting optimized route in Google Maps...");
      setShowSnackbar(true);
    };

    // Get current location (either from state or request it)
    if (currentLocation && currentLocation.lat && currentLocation.lng) {
      openGoogleMapsRoute(currentLocation.lat, currentLocation.lng);
    } else {
      // If current location is not available, try to get it now
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const {latitude, longitude} = position.coords;
            openGoogleMapsRoute(latitude, longitude);
          },
          (error) => {
            // If location access denied, optimize route from first location
            const firstLocation = selectedLocations[0];
            const startLat = firstLocation.location.lat;
            const startLng = firstLocation.location.lng;
            
            // Optimize remaining locations from the first location
            const remainingLocations = selectedLocations.slice(1);
            if (remainingLocations.length > 0) {
              const optimizedLocations = optimizeRoute(startLat, startLng, remainingLocations);
              
              let waypoints = "";
              optimizedLocations.forEach((location, index) => {
                const lat = location.location.lat;
                const lng = location.location.lng;
                waypoints += `${lat},${lng}`;
                if (index < optimizedLocations.length - 1) {
                  waypoints += "/";
                }
              });

              // Use proper format for auto-starting navigation
              const waypointsArray = waypoints.split('/');
              const destination = waypointsArray[waypointsArray.length - 1];
              const waypointsParam = waypointsArray.slice(0, -1).join('|');
              const googleMapsUrl = waypointsParam
                ? `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLng}&destination=${destination}&waypoints=${waypointsParam}&dir_action=navigate&travelmode=driving`
                : `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLng}&destination=${destination}&dir_action=navigate&travelmode=driving`;
              window.open(googleMapsUrl, "_blank");
            } else {
              // Only one location selected
              const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${startLat},${startLng}&dir_action=navigate&travelmode=driving`;
              window.open(googleMapsUrl, "_blank");
            }
            
            setSnackbarMessage("Starting optimized route in Google Maps...");
            setShowSnackbar(true);
          },
          {enableHighAccuracy: true, timeout: 5000}
        );
      } else {
        // Fallback: optimize route from first location
        const firstLocation = selectedLocations[0];
        const startLat = firstLocation.location.lat;
        const startLng = firstLocation.location.lng;
        
        const remainingLocations = selectedLocations.slice(1);
        if (remainingLocations.length > 0) {
          // Optimize remaining locations from the first location
          const optimizedLocations = optimizeRoute(startLat, startLng, remainingLocations);
          
          let waypoints = "";
          optimizedLocations.forEach((location, index) => {
            const lat = location.location.lat;
            const lng = location.location.lng;
            waypoints += `${lat},${lng}`;
            if (index < optimizedLocations.length - 1) {
              waypoints += "/";
            }
          });

          // Use Google Maps URL that auto-starts navigation on mobile
          const waypointsArray = waypoints.split('/');
          const destination = waypointsArray[waypointsArray.length - 1];
          const waypointsParam = waypointsArray.slice(0, -1).join('|');
          const googleMapsUrl = waypointsParam 
            ? `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLng}&destination=${destination}&waypoints=${waypointsParam}&dir_action=navigate&travelmode=driving`
            : `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLng}&destination=${destination}&dir_action=navigate&travelmode=driving`;
          window.open(googleMapsUrl, "_blank");
        } else {
          // Only one location selected
          const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${startLat},${startLng}&dir_action=navigate&travelmode=driving`;
          window.open(googleMapsUrl, "_blank");
        }
        
        setSnackbarMessage("Starting optimized route in Google Maps...");
        setShowSnackbar(true);
      }
    }
  };

  const handleDeleteHouse = async (houseToDelete) => {
    if (!user) {
      alert("You need to log in to delete a house.");
      return;
    }

    if (houseToDelete.ownerId !== user.uid) {
      alert("You can only delete houses you added.");
      return;
    }

    try {
      await deleteDoc(doc(db, "locations", houseToDelete.id));
      setHouses((prevHouses) =>
        prevHouses.filter((house) => house.id !== houseToDelete.id)
      );
      setSnackbarMessage("House deleted successfully!");
      setShowSnackbar(true);
    } catch (error) {
      alert("Failed to delete house. Please try again.");
    }
  };


  const handleCitySearch = () => {
    if (!autocomplete) {
      return;
    }

    const place = autocomplete.getPlace();
    if (!place || !place.geometry) {
      alert("Please select a valid city.");
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const newLocation = {lat, lng};

    // Update the map center
    setMapCenter(newLocation);
    setZoomLevel(13); // Set a higher zoom level for city search

    setCurrentLocation(newLocation);
  };

  const handleVote = async (house) => {
    if (!user) {
      alert("Please log in to vote for locations. You'll be redirected to the login page.");
      handleCloseRatingsModal();
      navigate("/login?redirect=/map");
      return;
    }

    try {
      // Check if user already voted for this location (matching app's behavior)
      const votesRef = collection(db, "votes");
      const q = query(
        votesRef,
        where("locationId", "==", house.id),
        where("userId", "==", user.uid)
      );
      const existingVotes = await getDocs(q);

      if (!existingVotes.empty) {
        alert("You have already voted for this location.");
        return;
      }

      // Record the vote in votes collection (matching app's structure)
      await addDoc(collection(db, "votes"), {
        locationId: house.id,
        userId: user.uid,
        createdAt: new Date().toISOString(),
      });

      // Increment vote count on location document (matching app's behavior)
      const houseDoc = doc(db, "locations", house.id);
      await updateDoc(houseDoc, {votes: increment(1)});

      // Update local state
      setHouses((prev) =>
        prev.map((h) =>
          h.id === house.id ? {...h, votes: (h.votes || 0) + 1} : h
        )
      );
      
      // Update ratingsHouse if it's the same location
      if (ratingsHouse && ratingsHouse.id === house.id) {
        setRatingsHouse({...ratingsHouse, votes: (ratingsHouse.votes || 0) + 1});
        setHasUserVoted(true);
      }
      
      setSnackbarMessage("Vote recorded!");
      setShowSnackbar(true);
    } catch (error) {
      alert("Failed to record vote. Please try again.");
    }
  };
  // MarkerLegend removed - categories are now filtered via CategoryDropdownWeb

  const handlePictureUpload = async (event, house) => {
    const file = event.target.files[0];
    if (!file) return;

    const storageRef = ref(storage, `house-images/${house.id}/${file.name}`);
    try {
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      const houseDoc = doc(db, "locations", house.id);
      await updateDoc(houseDoc, {
        pictures: arrayUnion(fileUrl), // Save picture URLs in an array
      });

      setSnackbarMessage("Picture uploaded successfully!");
      setShowSnackbar(true);
    } catch (error) {
      alert("Failed to upload picture. Please try again.");
    }
  };
  const filteredHouses = houses.filter((house) => {
    // Price filter (All/Free/Paid)
    // App uses isFree, website might have isPaid - handle both for compatibility
    const isFree = house.isFree !== undefined ? house.isFree : (house.isPaid !== undefined ? !house.isPaid : true);
    const matchesPaidFilter =
      filter === "all" || (filter === "paid" ? !isFree : isFree);

    if (!matchesPaidFilter) return false;

    // Category filter - use selectedCategories if available
    if (selectedCategories.length > 0 && !selectedCategories.includes("all")) {
      const houseCategory = (house.category || house.type || "all").toLowerCase();
      const matchesCategory = selectedCategories.some(cat => 
        cat.toLowerCase() === houseCategory
      );
      if (!matchesCategory) return false;
    }

    // Legacy legend filter (for backward compatibility)
    if (selectedLegend !== "all" && selectedLegend !== "selected") {
      const houseCategory = (house.category || house.type || "all").toLowerCase();
      if (houseCategory !== selectedLegend.toLowerCase()) {
        return false;
      }
    }

    return true;
  });

  return (
    <>
      {firebaseError && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: '#d32f2f',
            color: 'white',
            padding: '12px',
            textAlign: 'center',
            zIndex: 10000,
            fontFamily: "'Playfair Display', serif",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ⚠️ Firebase Configuration Error: {firebaseError}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', marginTop: '4px', opacity: 0.9 }}>
            Please check your Vercel environment variables. Open browser console for details.
          </Typography>
        </Box>
      )}
    <LoadScript
      googleMapsApiKey="AIzaSyBTz_8yJwYAQp90CczQ_tRwns6gemnuUtE"
      libraries={libraries}
    >
      <Routes>
        <Route
          path="/"
          element={
            <LandingPage 
              user={user}
              handleLogin={handleLogin}
              handleLogout={handleLogout}
              setAutocomplete={setAutocomplete}
              handleCitySearch={handleCitySearch}
            />
          } 
        />
        <Route
          path="/map"
          element={
            <div className="min-h-screen" style={{backgroundColor: "#FAFAFA"}}>
              <NavBar
                user={user}
                handleLogin={handleLogin}
                handleLogout={handleLogout}
                navigate={navigate}
                setAutocomplete={setAutocomplete}
                handleCitySearch={handleCitySearch}
              />

              <main className="px-4 flex flex-col items-center">
                {/* Filter Bar - Matching App Design Exactly */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                    padding: "8px 16px",
                    backgroundColor: "#fff",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                    marginBottom: "4px",
                  }}
                >
                  {/* Category Dropdown - Left Side (matches app) */}
                  <CategoryDropdownWeb
                    selectedCategories={selectedCategories}
                    onCategoryChange={setSelectedCategories}
                  />

                  {/* Price Filter Segmented Control - Right Side (matches app) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "3px",
                      height: "44px",
                      flex: 1,
                      maxWidth: "200px",
                    }}
                  >
                    {[
                      {id: "all", label: "All", icon: AllInclusiveIcon},
                      {id: "free", label: "Free", icon: FreeIcon},
                      {id: "paid", label: "Paid", icon: PaidIcon},
                    ].map((filterOption) => {
                      const Icon = filterOption.icon;
                      const isSelected = filter === filterOption.id;

                      return (
                        <button
                          key={filterOption.id}
                          onClick={() =>
                            handleFilterChange(null, filterOption.id)
                          }
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "8px",
                            border: "none",
                            backgroundColor: isSelected
                              ? "#fff"
                              : "transparent",
                            color: isSelected ? "#C41E3A" : "#666",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            boxShadow: isSelected
                              ? "0 1px 2px rgba(0, 0, 0, 0.1)"
                              : "none",
                            gap: "4px",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.target.style.backgroundColor =
                                "rgba(0, 0, 0, 0.05)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          <Icon sx={{fontSize: 16}} />
                          {filterOption.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Map Container */}
                <div className="w-full h-100 relative rounded-lg overflow-hidden shadow-lg mb-4">
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      padding: "5px 10px",
                      borderRadius: "5px",
                      fontSize: "9px",
                      fontWeight: "bold",
                      zIndex: 1000,
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    Click markers to view details • Use "Add to Route" button or Ctrl/Cmd+Click
                  </div>
                  <GoogleMap
                    mapContainerStyle={{
                      height: "75vh",
                    }}
                    center={mapCenter}
                    zoom={zoomLevel}
                    options={{
                      styles: mapStyles,
                      disableDefaultUI: true,
                      zoomControl: false,
                      fullscreenControl: true,
                      gestureHandling: "greedy",
                    }}
                  >
                    {/* Current Location Marker */}

                    {/* {currentLocation && (
                      <Marker
                        position={currentLocation}
                        title="Your Current Location"
                        icon={{
                          url: "https://img.icons8.com/emoji/48/000000/blue-circle-emoji.png",
                          scaledSize: new window.google.maps.Size(30, 30),
                        }}
                      />
                    )} */}
                    {/* House Markers - Only render when marker images are loaded */}
                    {markerImagesLoaded && filteredHouses
                      .filter(house => house.location && house.location.lat && house.location.lng)
                      .map((house) => {
                        const routeIndex = selectedLocations.findIndex(loc => loc.id === house.id);
                        const isInRoute = routeIndex !== -1;
                        const routeNumber = isInRoute ? routeIndex + 1 : null;
                        return (
                          <Marker
                            key={`${house.id}-${filter}-${selectedCategories.join(',')}`}
                            position={house.location}
                            onClick={(e) => {
                              // Immediately prevent any default behavior
                              if (e && e.domEvent) {
                                e.domEvent.preventDefault();
                                e.domEvent.stopPropagation();
                                if (e.domEvent.stopImmediatePropagation) {
                                  e.domEvent.stopImmediatePropagation();
                                }
                              }
                              // Use requestAnimationFrame to ensure event is handled before any navigation
                              requestAnimationFrame(() => {
                                handleMarkerClick(house, e);
                              });
                            }}
                            icon={{
                              url: getMarkerIcon(
                                house.category || house.type || "all",
                                false,
                                routeNumber
                              ),
                              scaledSize: new window.google.maps.Size(50, 50),
                            }}
                          />
                        );
                      })}


                  </GoogleMap>
                </div>
                
                {/* Route Planner Component */}
                <RoutePlanner
                  selectedLocations={selectedLocations}
                  onRemoveLocation={handleRemoveLocation}
                  onClearAll={handleClearAllLocations}
                  onOpenInGoogleMaps={handleOpenInGoogleMaps}
                  isOpen={routePlannerOpen}
                  onClose={() => setRoutePlannerOpen(false)}
                />

                {/* Route Planner Toggle FAB */}
                <Box
                  sx={{
                    position: "fixed",
                    bottom: { xs: "80px", sm: "100px" },
                    right: { xs: "16px", sm: "24px" },
                    zIndex: 999,
                  }}
                >
                  {/* Pulsing ring animation */}
                  {fabPulse && (
                    <>
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "64px",
                          height: "64px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(196, 30, 58, 0.3)",
                          animation: "pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                          pointerEvents: "none",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(196, 30, 58, 0.2)",
                          animation: "pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite 0.3s",
                          pointerEvents: "none",
                        }}
                      />
                    </>
                  )}
                  
                  <Fab
                    color="primary"
                    aria-label="plan route"
                    onClick={() => {
                      setRoutePlannerOpen(!routePlannerOpen);
                      setFabPulse(false); // Stop animation when clicked
                    }}
                    sx={{
                      position: "relative",
                      backgroundColor: selectedLocations.length > 0 ? "#4CAF50" : "#C41E3A",
                      color: "#FFFFFF",
                      boxShadow: fabPulse 
                        ? "0 0 0 0 rgba(196, 30, 58, 0.7), 0 6px 20px rgba(196, 30, 58, 0.5)"
                        : selectedLocations.length > 0
                        ? "0 6px 20px rgba(76, 175, 80, 0.5), 0 0 0 4px rgba(76, 175, 80, 0.2)"
                        : "0 4px 16px rgba(196, 30, 58, 0.4)",
                      animation: fabPulse 
                        ? "pulse-button 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite, bounce-attention 1.5s ease-in-out infinite" 
                        : selectedLocations.length > 0
                        ? "glow-highlight 2s ease-in-out infinite"
                        : "none",
                      border: selectedLocations.length > 0 ? "3px solid #FFFFFF" : "none",
                      zIndex: 1, // Lower z-index so badge appears on top
                      "&:hover": {
                        backgroundColor: selectedLocations.length > 0 ? "#45a049" : "#DC143C",
                        transform: "scale(1.15)",
                        boxShadow: selectedLocations.length > 0
                          ? "0 8px 24px rgba(76, 175, 80, 0.6), 0 0 0 6px rgba(76, 175, 80, 0.3)"
                          : "0 6px 20px rgba(196, 30, 58, 0.5)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <RouteIcon sx={{ fontSize: "28px" }} />
                  </Fab>
                  
                  {/* Location count badge - positioned at top right, in front of FAB */}
                  {selectedLocations.length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-12px",
                        right: "-12px",
                        backgroundColor: "#FFC107",
                        color: "#333",
                        borderRadius: "50%",
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: "'Playfair Display', serif",
                        fontWeight: 700,
                        fontSize: "0.9375rem",
                        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.3)",
                        border: "3px solid #FFFFFF",
                        zIndex: 10, // Higher z-index to appear on top of FAB
                        animation: selectedLocations.length > 0 ? "bounce-attention 2s ease-in-out infinite" : "none",
                        pointerEvents: "none", // Don't block clicks on FAB
                      }}
                    >
                      {selectedLocations.length}
                    </Box>
                  )}
                </Box>
                
                <Analytics />
                
                {/* Info Banner at Bottom - Dismissible */}
                {!bannerDismissed && (
                  <Box
                    sx={{
                      position: "fixed",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: "#C41E3A",
                      color: "#FFFFFF",
                      padding: { xs: "8px 12px", sm: "10px 20px" },
                      zIndex: 1000,
                      boxShadow: "0 -1px 8px rgba(0, 0, 0, 0.1)",
                      borderTop: "1px solid rgba(255, 255, 255, 0.15)",
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "1200px",
                        margin: "0 auto",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: { xs: "6px", sm: "10px" },
                        flexWrap: "nowrap",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                          fontWeight: 400,
                          opacity: 0.95,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Click on location markers for details
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                          fontWeight: 500,
                          opacity: 0.9,
                        }}
                      >
                        •
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                          fontWeight: 400,
                          opacity: 0.9,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Mobile app coming soon
                      </Box>
                      <IconButton
                        onClick={() => {
                          setBannerDismissed(true);
                          localStorage.setItem('infoBannerDismissed', 'true');
                        }}
                        size="small"
                        sx={{
                          color: "#FFFFFF",
                          padding: { xs: "4px", sm: "6px" },
                          marginLeft: { xs: "4px", sm: "8px" },
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: { xs: "16px", sm: "18px" } }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </main>
              <Snackbar
                open={showSnackbar}
                autoHideDuration={3000}
                onClose={() => setShowSnackbar(false)}
              >
                <Alert
                  onClose={() => setShowSnackbar(false)}
                  severity="success"
                  sx={{width: "100%"}}
                >
                  {snackbarMessage}
                </Alert>
              </Snackbar>
            </div>
          }
        />

        <Route
          path="/add-house"
          element={
            <div className="min-h-screen" style={{backgroundColor: "#fafafa"}}>
              <NavBar
                user={user}
                handleLogin={handleLogin}
                handleLogout={handleLogout}
                navigate={navigate}
                setAutocomplete={setAutocomplete}
                handleCitySearch={handleCitySearch}
              />
              <Box sx={{ padding: { xs: 2, sm: 4 }, maxWidth: 1200, margin: "auto" }}>
            <AddHouseForm
              onAddHouse={handleAddHouse}
                  onClose={() => navigate("/map")}
            />
              </Box>
                              </div>
          }
        />
        <Route
          path="/help" 
          element={
            <div className="min-h-screen" style={{backgroundColor: "#FAFAFA"}}>
              <NavBar
                user={user}
                handleLogin={handleLogin}
                handleLogout={handleLogout}
                navigate={navigate}
                setAutocomplete={setAutocomplete}
                handleCitySearch={handleCitySearch}
              />
              <HelpPage />
                  </div>
          } 
        />
        <Route
          path="/top-picks"
          element={
            <div className="min-h-screen" style={{backgroundColor: "#FAFAFA"}}>
              <NavBar
                user={user}
                handleLogin={handleLogin}
                handleLogout={handleLogout}
                navigate={navigate}
                setAutocomplete={setAutocomplete}
                handleCitySearch={handleCitySearch}
              />
              <TopPicks houses={houses} onLocationClick={handleViewRatings} />
                </div>
          }
        />
        <Route
          path="/login"
          element={
            <div className="min-h-screen" style={{backgroundColor: "#FAFAFA"}}>
              <NavBar
                user={user}
                handleLogin={handleLogin}
                handleLogout={handleLogout}
                navigate={navigate}
                setAutocomplete={setAutocomplete}
                handleCitySearch={handleCitySearch}
              />
              <Login onLogin={(user) => setUser(user)} />
            </div>
          }
        />
      </Routes>
      
      {/* Footer - Appears on all pages */}
      <Footer />
      
      {/* Location Info Modal - Available on all pages */}
                {ratingsModalOpen && ratingsHouse && (
                  <Dialog
                    open={ratingsModalOpen}
                    onClose={handleCloseRatingsModal}
                    fullWidth
                    fullScreen={false}
                    maxWidth="sm"
                    PaperProps={{
                      sx: {
                        borderRadius: { xs: "16px 16px 0 0", sm: "16px" },
                        padding: 0,
                        maxHeight: { xs: "90vh", sm: "90vh" },
                        margin: { xs: "auto 0 0 0", sm: "32px" },
                        marginTop: { xs: "10vh", sm: "32px" },
                        display: "flex",
                        flexDirection: "column",
                        position: { xs: "fixed", sm: "relative" },
                        bottom: { xs: 0, sm: "auto" },
                        left: { xs: 0, sm: "auto" },
                        right: { xs: 0, sm: "auto" },
                        width: { xs: "100%", sm: "auto" },
                      },
                    }}
                  >
          {/* Header with Add to Route Button - Mobile Optimized */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#C41E3A",
              color: "#FFFFFF",
              padding: { xs: "12px 16px", sm: "20px" },
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              flexShrink: 0,
            }}
          >
            {/* Top Row: Close Button and Add to Route */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: { xs: "8px", sm: "12px" }, gap: "8px" }}>
              <IconButton
                onClick={handleCloseRatingsModal}
                sx={{
                  color: "#FFFFFF",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                  },
                  padding: { xs: "8px", sm: "10px" },
                }}
              >
                <CloseIcon />
              </IconButton>
              
              {ratingsHouse && (() => {
                const isInRoute = selectedLocations.some(loc => loc.id === ratingsHouse.id);
                return (
                  <Button
                    onTouchEnd={(e) => {
                      // Handle touch events on mobile - this fires before click
                      e.stopPropagation();
                      e.preventDefault();
                      handleAddToRouteFromDialog(ratingsHouse, e, true);
                    }}
                    onClick={(e) => {
                      // For desktop/mouse clicks only
                      // On mobile, touch events are handled above
                      if (e.type === 'click' && (e.nativeEvent?.pointerType === 'mouse' || !('ontouchstart' in window))) {
                        e.stopPropagation();
                        e.preventDefault();
                        handleAddToRouteFromDialog(ratingsHouse, e, false);
                      } else {
                        // Prevent click from firing after touch on mobile
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                    variant="contained"
                    startIcon={<RouteIcon />}
                    sx={{
                      backgroundColor: "#4CAF50",
                      color: "#FFFFFF",
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      fontSize: { xs: "0.75rem", sm: "0.9375rem" },
                      padding: { xs: "6px 12px", sm: "10px 20px" },
                      borderRadius: "8px",
                      textTransform: "none",
                      boxShadow: "0 2px 8px rgba(76, 175, 80, 0.4)",
                      border: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      touchAction: "manipulation", // Prevents double-tap zoom on iOS
                      WebkitTapHighlightColor: "transparent", // Removes tap highlight on iOS
                      "&:hover:not(:disabled)": {
                        backgroundColor: "#45a049",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
                      },
                      "&:disabled": {
                        backgroundColor: "#4CAF50",
                        opacity: 0.7,
                        cursor: "not-allowed",
                      },
                      transition: "all 0.15s ease",
                    }}
                  >
                    {isInRoute ? "✓ In Route" : "Add to Route"}
                  </Button>
                );
              })()}
            </Box>
            
            {/* Location Name */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "1.1rem", sm: "1.5rem" },
                fontWeight: 700,
                color: "#FFFFFF",
                marginBottom: { xs: "6px", sm: "8px" },
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {ratingsHouse?.name || ratingsHouse?.address || "Location Details"}
            </Typography>
            
            {/* Chips Row */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
              {ratingsHouse?.category && (
                <Chip
                  icon={<CategoryIcon sx={{ fontSize: "14px", color: "inherit" }} />}
                  label={ratingsHouse.category.charAt(0).toUpperCase() + ratingsHouse.category.slice(1)}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    height: { xs: "24px", sm: "28px" },
                    "& .MuiChip-icon": {
                      color: "#FFFFFF",
                    },
                  }}
                />
              )}
              {ratingsHouse?.isFree !== undefined && (
                <Chip
                  label={ratingsHouse.isFree ? "Free" : "Paid"}
                  size="small"
                  sx={{
                    backgroundColor: ratingsHouse.isFree 
                      ? "rgba(76, 175, 80, 0.9)" 
                      : "rgba(255, 152, 0, 0.9)",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    height: { xs: "24px", sm: "28px" },
                  }}
                />
              )}
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "4px",
                marginLeft: "auto",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                padding: "4px 8px",
                borderRadius: "12px",
              }}>
                <StarIcon sx={{ fontSize: { xs: "16px", sm: "18px" }, color: "#FFC107" }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#FFFFFF",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  {ratingsHouse.votes || 0}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Address Section - Below Header */}
          {ratingsHouse?.address && (
            <Box 
              sx={{ 
                padding: { xs: "12px 16px", sm: "16px 20px" },
                backgroundColor: "#FAFAFA",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                <LocationOnIcon sx={{ fontSize: { xs: "20px", sm: "22px" }, color: "#C41E3A", marginTop: "2px", flexShrink: 0 }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: "'Inter', sans-serif",
                    color: "#666",
                    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                    lineHeight: 1.5,
                  }}
                >
                  {ratingsHouse.address}
                </Typography>
              </Box>
            </Box>
          )}

          <DialogContent 
            sx={{ 
              padding: { xs: "16px", sm: "20px" },
              paddingBottom: { xs: "24px", sm: "24px" },
              flex: 1,
              overflowY: "auto",
              backgroundColor: "#FFFFFF",
              minHeight: 0, // Allows flexbox to shrink
            }}
          >
            {/* Image Gallery */}
            {(ratingsHouse?.pictures?.length > 0 || ratingsHouse?.images?.length > 0) && (
              <Box sx={{ marginBottom: { xs: "20px", sm: "24px" } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: { xs: "10px", sm: "12px" },
                  }}
                >
                  Photos
                </Typography>
                <Grid container spacing={1.5}>
                  {(ratingsHouse?.pictures || ratingsHouse?.images || []).map((imageUrl, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={`Location photo ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: { xs: "180px", sm: "220px" },
                          objectFit: "cover",
                          borderRadius: { xs: "8px", sm: "12px" },
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                          pointerEvents: "none",
                          userSelect: "none",
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Details Grid */}
            <Grid container spacing={1.5} sx={{ marginBottom: { xs: "20px", sm: "24px" } }}>
              {ratingsHouse?.hours && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: { xs: "10px", sm: "12px" },
                      padding: { xs: "14px", sm: "16px" },
                      backgroundColor: "#F9F9F9",
                      borderRadius: { xs: "10px", sm: "12px" },
                      border: "1px solid #E8E8E8",
                    }}
                  >
                    <AccessTimeIcon sx={{ color: "#C41E3A", fontSize: { xs: "22px", sm: "24px" }, marginTop: "2px", flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: "#999", 
                          fontFamily: "'Inter', sans-serif",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Hours
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500, 
                          color: "#333", 
                          fontFamily: "'Inter', sans-serif",
                          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                          lineHeight: 1.5,
                        }}
                      >
                        {ratingsHouse.hours}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              
              {ratingsHouse?.website && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: { xs: "10px", sm: "12px" },
                      padding: { xs: "14px", sm: "16px" },
                      backgroundColor: "#F9F9F9",
                      borderRadius: { xs: "10px", sm: "12px" },
                      border: "1px solid #E8E8E8",
                    }}
                  >
                    <LanguageIcon sx={{ color: "#C41E3A", fontSize: { xs: "22px", sm: "24px" }, marginTop: "2px", flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: "#999", 
                          fontFamily: "'Inter', sans-serif",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Website
                      </Typography>
                      <Button
                        href={ratingsHouse.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{
                          textTransform: "none",
                          color: "#C41E3A",
                          fontWeight: 600,
                          padding: 0,
                          fontFamily: "'Inter', sans-serif",
                          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                          justifyContent: "flex-start",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Visit Website →
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* Description */}
            {ratingsHouse?.description && (
              <Box sx={{ marginBottom: { xs: "20px", sm: "24px" } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: { xs: "10px", sm: "12px" },
                  }}
                >
                  About
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    color: "#555",
                    lineHeight: 1.7,
                    padding: { xs: "14px", sm: "16px" },
                    backgroundColor: "#F9F9F9",
                    borderRadius: { xs: "10px", sm: "12px" },
                    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                  }}
                >
                  {ratingsHouse.description}
                </Typography>
              </Box>
            )}

            <Divider sx={{ marginY: { xs: "20px", sm: "24px" } }} />

            {/* Voting Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "12px", sm: "16px" },
                marginBottom: { xs: "20px", sm: "24px" },
                padding: { xs: "14px", sm: "16px" },
                backgroundColor: "#F9F9F9",
                borderRadius: { xs: "10px", sm: "12px" },
                border: "1px solid #E8E8E8",
              }}
            >
              {user ? (
                <>
                        <Button
                          onClick={() => handleVote(ratingsHouse)}
                          variant="contained"
                          disabled={hasUserVoted}
                          sx={{
                            width: { xs: "100%", sm: "auto" },
                            backgroundColor: hasUserVoted ? "#999" : "#C41E3A",
                            color: "#FFFFFF",
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 600,
                            padding: { xs: "12px 24px", sm: "10px 24px" },
                            borderRadius: "8px",
                            textTransform: "none",
                            fontSize: { xs: "0.9375rem", sm: "1rem" },
                            "&:hover": {
                              backgroundColor: hasUserVoted ? "#999" : "#DC143C",
                            },
                          }}
                          startIcon={<ThumbUpAltIcon />}
                        >
                          {hasUserVoted ? "Already Voted" : "Vote"}
                        </Button>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 600,
                            color: "#333",
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                          }}
                        >
                          {ratingsHouse.votes || 0} Votes
                        </Typography>
                </>
              ) : (
                <Box sx={{ textAlign: "center", width: "100%" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      color: "#666",
                      marginBottom: "8px",
                    }}
                  >
                    Please log in to vote for this location
                  </Typography>
                  <Button
                    onClick={() => {
                      handleCloseRatingsModal();
                      navigate("/login?redirect=/top-picks");
                    }}
                    variant="outlined"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      color: "#C41E3A",
                      borderColor: "#C41E3A",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#DC143C",
                        backgroundColor: "rgba(196, 30, 58, 0.1)",
                      },
                    }}
                  >
                    Log In to Vote
                  </Button>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 600,
                      color: "#333",
                      marginTop: "8px",
                    }}
                  >
                    {ratingsHouse.votes || 0} Votes
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Reviews Section */}
            <Box sx={{ marginBottom: { xs: "20px", sm: "24px" } }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 600,
                  color: "#333",
                  marginBottom: { xs: "10px", sm: "12px" },
                }}
              >
                Reviews ({locationReviews.length || 0})
              </Typography>
              <Box
                sx={{
                  maxHeight: { xs: "180px", sm: "200px" },
                  overflowY: "auto",
                  backgroundColor: "#F9F9F9",
                  padding: { xs: "10px", sm: "12px" },
                  borderRadius: { xs: "10px", sm: "12px" },
                  border: "1px solid #E8E8E8",
                }}
              >
                {locationReviews && locationReviews.length > 0 ? (
                  locationReviews.map((reviewItem) => (
                    <Box
                      key={reviewItem.id}
                      sx={{
                        backgroundColor: "#FFFFFF",
                        padding: { xs: "10px", sm: "12px" },
                        borderRadius: { xs: "8px", sm: "10px" },
                        marginBottom: { xs: "8px", sm: "10px" },
                        borderLeft: "3px solid #C41E3A",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          color: "#555",
                          lineHeight: 1.6,
                          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                        }}
                      >
                        {reviewItem.comment || reviewItem.text || reviewItem}
                      </Typography>
                      {reviewItem.photoUrl && (
                        <Box
                          component="img"
                          src={reviewItem.photoUrl}
                          alt="Review photo"
                          sx={{
                            width: "100%",
                            maxWidth: "300px",
                            marginTop: "8px",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(reviewItem.photoUrl, "_blank");
                          }}
                        />
                      )}
                    </Box>
                          ))
                        ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      color: "#999",
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                            No reviews yet. Be the first to add one!
                  </Typography>
                        )}
              </Box>
            </Box>

            {/* Add Review Section */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 600,
                  color: "#333",
                  marginBottom: { xs: "10px", sm: "12px" },
                }}
              >
                Add a Review
              </Typography>
              {user ? (
                      <TextField
                        value={review}
                        onChange={handleReviewChange}
                  placeholder="Share your thoughts about this location..."
                        fullWidth
                        multiline
                  rows={4}
                        variant="outlined"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#C41E3A",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C41E3A",
                      },
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    padding: "20px",
                    backgroundColor: "#F9F9F9",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      color: "#666",
                      marginBottom: "12px",
                    }}
                  >
                    Please log in to leave a review
                  </Typography>
                      <Button
                    onClick={() => {
                      handleCloseRatingsModal();
                      navigate("/login?redirect=/top-picks");
                    }}
                        variant="contained"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      backgroundColor: "#C41E3A",
                      color: "#FFFFFF",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "#DC143C",
                      },
                    }}
                  >
                    Log In to Review
                      </Button>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              position: "sticky",
              bottom: 0,
              padding: { xs: "12px 16px", sm: "16px 24px" },
              borderTop: "1px solid #E0E0E0",
              backgroundColor: "#FFFFFF",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: { xs: "10px", sm: "8px" },
              zIndex: 10,
              boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Button
              onClick={handleCloseRatingsModal}
              variant="outlined"
              sx={{
                fontFamily: "'Inter', sans-serif",
                color: "#666",
                borderColor: "#E0E0E0",
                textTransform: "none",
                fontWeight: 600,
                padding: { xs: "10px", sm: "8px 16px" },
                fontSize: { xs: "0.9375rem", sm: "1rem" },
                width: { xs: "100%", sm: "auto" },
                "&:hover": {
                  borderColor: "#C41E3A",
                  color: "#C41E3A",
                  backgroundColor: "rgba(196, 30, 58, 0.05)",
                },
              }}
            >
              Close
            </Button>
            {user && (
              <Button
                onClick={handleAddReview}
                variant="contained"
                disabled={!review.trim()}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  fontFamily: "'Playfair Display', serif",
                  backgroundColor: "#C41E3A",
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 600,
                  padding: { xs: "12px", sm: "10px 24px" },
                  fontSize: { xs: "0.9375rem", sm: "1rem" },
                  "&:hover": {
                    backgroundColor: "#DC143C",
                  },
                  "&:disabled": {
                    backgroundColor: "#E0E0E0",
                    color: "#999",
                  },
                }}
              >
                Submit Review
              </Button>
            )}
          </DialogActions>
                </Dialog>
      )}
    </LoadScript>
    </>
  );
};

export default App;
